/*
 * lm-stage.js: the location map customizer's preview stage. The star map's
 * stage (starmap-v2.js, 2026-09-25), by way of the moon's (moon-stage.js),
 * on the location map DOM:
 *
 *   - phones: the site header bar collapses and its menu + cart buttons move
 *     onto the preview; the preview is sticky and collapses with the scroll
 *     (the print scales into the part still showing, no layout per frame);
 *     a minimise chevron; the notch band that fills the iOS status bar area.
 *     While the preview is shrunk the map is locked (a drag on a scaled map
 *     lands in the wrong place): tapping it scrolls back up to full size.
 *   - Room view, on request only: the print laid onto a photographed poster
 *     (matrix3d). Mapbox can't track gestures in perspective, so in the room
 *     its handlers pause and lm-stage takes pan / pinch / rotate / tilt /
 *     wheel and icon drags itself, mapped back through the room's transform.
 *   - Full-screen viewer with zoom, pinch, pan and swipe-down to close.
 *   - Light / dark theme toggle (initial theme resolved in layout/theme.liquid).
 *   - The add-to-cart star trail, called from locationmap.js's submit.
 *
 * Roles: .lm-main.lm-stage is the panel (star: .sm2-main), #lm-print-frame-wrap
 * the frame wrap that moves and scales (star: .sm2-frame-wrap; it also carries
 * the gift overlays), and #lm-print-frame the print (star: .sm2-frame).
 *
 * UI only: nothing here touches the design state. The map lock goes through
 * window.lmStageLock (locationmap.js), which never changes the customer's own
 * lock.
 */
(function () {
  'use strict';
  if (window.LMStage) return;

  // Same breakpoint as locationmap-section.css's stacked (phone / tablet) layout.
  var MOBILE_MQ = '(max-width: 991px)';

  // Shared between the collapse and Room view, as in starmap-v2.js.
  var roomCollapse = null;     // Room view's scene geometry for the collapse
  var onRoomLayout = null;     // Room view re-laid out: re-apply the collapse
  var roomRelayout = null;     // the collapse depth changed: re-lay Room view
  var roomCheck = null;        // re-map Room view if the print moved
  var getCollapseD = null;     // current collapse depth (px)

  function mobileNow() {
    return !!(window.matchMedia && window.matchMedia(MOBILE_MQ).matches);
  }
  // The 3D mockups (customizer-3d-core.js) mark their container .lm-3d-active.
  function threeDActive() {
    return !!document.querySelector('.lm-main .lm-3d-active');
  }
  // Copy the Mapbox canvas into a 2D canvas. The map is supersampled (2-3x
  // its box, times the screen's pixel ratio), and on an iPhone that exceeds
  // what WebGL will allocate: the drawing buffer comes out smaller than
  // canvas.width/height and holds the WHOLE view at that lower resolution.
  // Drawing the canvas "whole" then reads only the buffer's corner, scaled
  // up (a zoomed-in crop). Take the drawing buffer's own rect as the source.
  function copyMapCanvas(src, ctx, dw, dh) {
    var gl = null;
    try { gl = src.getContext('webgl2') || src.getContext('webgl'); } catch (e) { /* noop */ }
    var sw = (gl && gl.drawingBufferWidth) || src.width;
    var sh = (gl && gl.drawingBufferHeight) || src.height;
    ctx.drawImage(src, 0, 0, sw, sh, 0, 0, dw, dh);
  }

  // Gesture lock on the live map for the stage's own reasons (shrunk, Room
  // view), on top of whatever the customer's lock button says.
  function stageLock(reason, on) {
    if (typeof window.lmStageLock === 'function') window.lmStageLock(reason, on);
  }

  // ── Sticky, scroll-linked preview (phones, stacked layout) ─────────────
  function bindMobilePreview(main) {
    var toggle = document.getElementById('lm-preview-toggle');
    if (!toggle) return;
    var siteHeader = document.querySelector('.shopify-section-header');
    var frameWrap = document.getElementById('lm-print-frame-wrap');
    // The follow-along preview is deliberately compact: 76% leaves materially
    // more room for the controls below; scrolling back to the top restores it.
    var STUCK_FRAME_SCALE = 0.76;
    var stickyPaintQueued = false;
    var headerTrackUntil = 0;
    var expandedHeightTimer = null;
    var headerPositionCache = null;
    var animTimer = null;

    // ── Notch backdrop ──────────────────────────────────────────────────
    // Safari 26 draws only the page's normal scrolling content under the
    // status bar (fixed and sticky layers are clipped at its edge), so this
    // is in-flow content (see .lm-notch-band in the CSS) kept parked just
    // above the viewport, under the preview. Where CSS scroll-driven
    // animations exist it rides the scroll inside the engine, in step with
    // the sticky preview; otherwise script moves it, a frame behind.
    var notchBand = document.querySelector('body > .lm-notch-band');
    if (!notchBand) {
      notchBand = document.createElement('div');
      notchBand.className = 'lm-notch-band';
      notchBand.setAttribute('aria-hidden', 'true');
      document.body.appendChild(notchBand);
    }
    var NOTCH_REACH = 300;   // px above the viewport top (also in the CSS keyframes)
    var NOTCH_SCROLL_LINKED = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline', 'scroll()'));
    notchBand.classList.toggle('is-scroll-linked', NOTCH_SCROLL_LINKED);
    var lastBandY = null, lastBandH = null;

    // In Room view the band carries a copy of the room photo, laid exactly
    // over the real one, so under the notch the scene simply carries on.
    var bandImg = null, lastBandImgT = null, lastBandImgSize = null;
    function paintBandPhoto(show, mainRect, p) {
      var g = show ? roomCollapse : null;
      if (!g || !g.photo) {
        if (bandImg && bandImg.style.display !== 'none') bandImg.style.display = 'none';
        return;
      }
      if (!bandImg) {
        bandImg = document.createElement('img');
        bandImg.className = 'lm-notch-band__img';
        bandImg.alt = '';
        bandImg.decoding = 'async';
        notchBand.appendChild(bandImg);
      }
      var ph = g.photo, e = g.end;
      if (bandImg.getAttribute('src') !== ph.src) bandImg.setAttribute('src', ph.src);
      var size = Math.round(ph.w) + 'x' + Math.round(ph.h);
      if (size !== lastBandImgSize) {
        lastBandImgSize = size;
        bandImg.style.width = ph.w + 'px';
        bandImg.style.height = ph.h + 'px';
      }
      // Same map applyCollapse gives the real photo (P -> kP + p*t in the
      // panel's padding box), then into the band's coordinates.
      var k = 1 + p * (e.k - 1);
      var bandTop = NOTCH_SCROLL_LINKED ? -NOTCH_REACH
        : lastBandY - (window.pageYOffset || document.documentElement.scrollTop || 0);
      var x = mainRect.left + main.clientLeft + k * ph.left + p * e.tx;
      var y = mainRect.top + main.clientTop + k * ph.top + p * e.ty - bandTop;
      var t = 'translate3d(' + x.toFixed(2) + 'px,' + y.toFixed(2) + 'px,0) scale(' + (+k.toFixed(5)) + ')';
      if (t !== lastBandImgT) { lastBandImgT = t; bandImg.style.transform = t; }
      if (bandImg.style.display === 'none') bandImg.style.display = '';
    }

    // iOS 26 Safari can leave the scroll-linked animation behind when the
    // page shortens under the reader (e.g. "Show fewer themes" clamps the
    // scroll): the band then sits hundreds of px too low and its room photo
    // shows over the sidebar. The band is already laid out here (mainRect was
    // just read), so checking it is cheap: restart the animation once, and if
    // it is still off, follow the scroll from JS for the rest of the visit.
    // Armed only once the band has been seen in place: on the first frames
    // the animation has not applied yet (transform none), which is not drift.
    var bandSeenInPlace = false;
    function checkBandTimeline() {
      if (!NOTCH_SCROLL_LINKED || !notchBand.getClientRects().length) return;   // hidden (landscape)
      if (Math.abs(notchBand.getBoundingClientRect().top + NOTCH_REACH) <= 3) { bandSeenInPlace = true; return; }
      if (!bandSeenInPlace) return;
      notchBand.style.animation = 'none';
      void notchBand.offsetWidth;
      notchBand.style.animation = '';
      if (Math.abs(notchBand.getBoundingClientRect().top + NOTCH_REACH) <= 3) return;
      NOTCH_SCROLL_LINKED = false;
      notchBand.classList.remove('is-scroll-linked');
      lastBandY = null;
      lastBandImgT = null;
    }

    function paintNotchBand(minimized, mainRect, p) {
      checkBandTimeline();
      // Reach below the viewport edge, hidden behind the preview; never past
      // its bottom (minimised it is ~49px; at the end of the page the preview
      // scrolls away and the band must not be left showing over the footer).
      var below = Math.min(minimized ? 40 : 200, Math.round(mainRect.bottom) - (minimized ? 9 : 80));
      var h = Math.max(0, NOTCH_REACH + below);
      if (h !== lastBandH) { lastBandH = h; notchBand.style.height = h + 'px'; }
      if (!NOTCH_SCROLL_LINKED) {
        var y = Math.round((window.pageYOffset || document.documentElement.scrollTop || 0) - NOTCH_REACH);
        if (y !== lastBandY) { lastBandY = y; notchBand.style.transform = 'translate3d(0,' + y + 'px,0)'; }
      }
      var room = main.classList.contains('lm-stage--room') && !minimized && !main.classList.contains('sm-gallery-viewing');
      if (notchBand.classList.contains('is-room') !== room) notchBand.classList.toggle('is-room', room);
      paintBandPhoto(room, mainRect, p);
    }

    // The theme's <sticky-header> keeps a scroll listener that toggles
    // -sticky/-hidden/animate on the header. On phones the header is pinned
    // static (inline style in locationmap.liquid), so that listener only adds
    // scroll-time work and class churn. Swapping the element for a plain div
    // fires its disconnectedCallback, which tears the listener down for good.
    // Desktop keeps the theme's sticky header as before.
    if (mobileNow()) {
      var stickyHeaderEl = document.querySelector('sticky-header');
      if (stickyHeaderEl) {
        var plain = document.createElement('div');
        plain.className = stickyHeaderEl.className;
        for (var i = 0; i < stickyHeaderEl.attributes.length; i++) {
          var attr = stickyHeaderEl.attributes[i];
          if (attr.name !== 'class') plain.setAttribute(attr.name, attr.value);
        }
        while (stickyHeaderEl.firstChild) plain.appendChild(stickyHeaderEl.firstChild);
        stickyHeaderEl.replaceWith(plain);
      }
      if (siteHeader) {
        siteHeader.classList.remove('shopify-section-header-sticky', 'shopify-section-header-hidden', 'animate');
        document.body.classList.remove('shopify-body-section-header-sticky', 'shopify-body-section-header-hidden');
      }
    }

    function paintToggle(minimized) {
      if (expandedHeightTimer) {
        window.clearTimeout(expandedHeightTimer);
        expandedHeightTimer = null;
      }
      main.classList.toggle('lm-stage--minimized', minimized);
      toggle.setAttribute('aria-expanded', minimized ? 'false' : 'true');
      toggle.setAttribute('aria-label', minimized ? 'Show preview' : 'Minimise preview');
      if (!minimized && main.style.getPropertyValue('--lm-preview-expanded-height')) {
        expandedHeightTimer = window.setTimeout(function () {
          main.style.removeProperty('--lm-preview-expanded-height');
          expandedHeightTimer = null;
        }, 340);
      }
    }

    var forceNextApply = false;
    function queueStickyPaint(trackHeader, force) {
      if (trackHeader) headerTrackUntil = Date.now() + 450;
      if (force) forceNextApply = true;
      if (stickyPaintQueued) return;
      stickyPaintQueued = true;
      window.requestAnimationFrame(paintStickyState);
    }

    // Last values written to the CSS custom properties: writing one
    // invalidates style for the subtree whether or not it changed, so only
    // write on change.
    var lastStickyTop = null;
    var lastCollapseD = null;
    var lastFrameBottom = null;
    var lastStripBtn = null;

    // ── Scroll-linked collapse ───────────────────────────────────────────
    // The panel keeps ONE layout and sticks --lm-collapse px above the
    // viewport (see .lm-stage in the CSS), so for the first D px of scroll
    // it slides under the top edge with the page, and the print (or the
    // whole room scene) is scaled into the part still showing. Only
    // `translate`/`scale` change per frame: no layout, and the controls below
    // move exactly with the finger.
    // Stuck-state paddings the compact preview is built around: 6px above
    // #print, 6px + the 16px sheet lip below it.
    var STUCK_PAD_TOP = 6;
    var STUCK_PAD_BOTTOM = 22;
    var stackedMq = window.matchMedia ? window.matchMedia(MOBILE_MQ) : null;
    var shortLandscapeMq = window.matchMedia ? window.matchMedia('(orientation: landscape) and (max-height: 500px)') : null;
    var geom = null;
    var geomDirty = true;
    var lastP = -1;
    var lastRoomKey = null;
    var willChangeTimer = null;
    var collapseEls = null;

    function kids(sel) {
      return Array.prototype.filter.call(main.children, function (el) { return el.matches(sel); });
    }
    function collectEls() {
      collapseEls = {
        // Pinned to the panel's top edge: ride down with the visible top.
        top: kids('.lm-chrome-btn, .sm-gallery-mobile-toggle, .lm-3d-toggle-btn, .lm-landmark-mobile-toggle'),
        // Centred on the panel: follow the centre of what is visible.
        middle: kids('.sm-gallery'),
        // Sit on the print's bottom edge (--lm-frame-bottom). The map strip,
        // the add-icon pin and the icon card fade out as the preview shrinks
        // (the map is locked then), so they need no motion.
        bottom: kids('.lm-toolbar-fab, .lm-floating-toolbar, .lm-room-fab, .lm-expand-fab, .lm-preview-toggle'),
        count: main.childElementCount
      };
    }

    function measureCollapse() {
      geomDirty = false;
      if (!frameWrap || !frameWrap.offsetHeight) return;
      var H = main.offsetHeight;
      var fw = frameWrap.offsetWidth, fh = frameWrap.offsetHeight;
      var ox = frameWrap.offsetLeft, oy = frameWrap.offsetTop;
      var k = STUCK_FRAME_SCALE;
      var D = Math.max(0, Math.round(H - STUCK_PAD_TOP - k * fh - STUCK_PAD_BOTTOM));
      // Flat end state as a similarity P -> kP + t in panel coordinates: the
      // print at 76%, centred, its top STUCK_PAD_TOP below the visible top (D).
      var Ox = ox + fw / 2, Oy = oy + fh / 2;   // #print scales about its centre
      geom = {
        H: H,
        D: D,
        flat: { k: k, tx: (1 - k) * Ox, ty: D + STUCK_PAD_TOP + k * fh / 2 - k * Oy },
        flatOrigin: [Ox, Oy],
        bottom0: Math.max(6, Math.round(H - (oy + fh))),
        bottom1: Math.max(6, Math.round(H - (D + STUCK_PAD_TOP + k * fh)))
      };
    }

    function setMotion(el, tx, ty, k) {
      var t = (Math.abs(tx) < 0.01 && Math.abs(ty) < 0.01) ? '' : (tx.toFixed(2) + 'px ' + ty.toFixed(2) + 'px');
      if (el.style.translate !== t) el.style.translate = t;
      if (k !== undefined) {
        var sc = Math.abs(k - 1) < 0.0001 ? '' : String(+k.toFixed(5));
        if (el.style.scale !== sc) el.style.scale = sc;
      }
    }

    function applyCollapse(p, force) {
      var roomGeom = main.classList.contains('lm-stage--room') ? roomCollapse : null;
      var roomKey = roomGeom ? roomGeom.key : 'flat';
      if (!force && Math.abs(p - lastP) < 0.0005 && roomKey === lastRoomKey) return;
      lastP = p;
      lastRoomKey = roomKey;
      // starmap-gallery.js and the 3D toggle add children after this binds:
      // re-collect when the panel's children change.
      if (!collapseEls || collapseEls.count !== main.childElementCount) collectEls();
      var D = geom ? geom.D : 0;

      // The picture: the print alone, or in Room view the scene, its light map
      // and the print lying in it, moved as one rigid piece. Each element has
      // its own transform-origin O, so the shared map P -> kP + t becomes
      // translate = t - (1 - k)O per element.
      var end = roomGeom ? roomGeom.end : (geom ? geom.flat : null);
      var items = roomGeom ? roomGeom.items : (geom && frameWrap ? [[frameWrap, geom.flatOrigin[0], geom.flatOrigin[1]]] : []);
      if (!roomGeom && roomCollapse) {
        // Room view just switched off: leave nothing on the scene layers.
        roomCollapse.items.forEach(function (it) { if (it[0] !== frameWrap) setMotion(it[0], 0, 0, 1); });
      }
      if (end) {
        var k = 1 + p * (end.k - 1);
        items.forEach(function (it) {
          setMotion(it[0], p * end.tx - (1 - k) * it[1], p * end.ty - (1 - k) * it[2], k);
        });
      }

      collapseEls.top.forEach(function (el) { setMotion(el, 0, p * D); });
      collapseEls.middle.forEach(function (el) { setMotion(el, 0, p * D / 2); });
      var drop = (!roomGeom && geom) ? p * (geom.bottom0 - geom.bottom1) : 0;
      collapseEls.bottom.forEach(function (el) { setMotion(el, 0, drop); });

      // A composited layer for the picture while it moves, dropped 180ms after
      // it stops so the browser re-rasters it sharp at the scale it settled on.
      items.forEach(function (it) { if (it[0].style.willChange !== 'transform') it[0].style.willChange = 'transform'; });
      if (willChangeTimer) window.clearTimeout(willChangeTimer);
      willChangeTimer = window.setTimeout(function () {
        willChangeTimer = null;
        [frameWrap].concat(roomCollapse ? roomCollapse.items.map(function (it) { return it[0]; }) : [])
          .forEach(function (el) { if (el) el.style.willChange = ''; });
      }, 180);
    }
    // Room view re-lays its scene out (resize, finish, size change); re-apply.
    onRoomLayout = function () { queueStickyPaint(false, true); };
    getCollapseD = function () { return geom ? geom.D : 0; };

    // The print leaves the stage in the gallery viewer and in 3D: no collapse
    // then, the whole panel pins at the top edge (like minimised).
    function printAway() {
      return main.classList.contains('sm-gallery-viewing') || threeDActive();
    }

    function paintStickyState() {
      stickyPaintQueued = false;
      var mobile = mobileNow();
      var stacked = !!(stackedMq && stackedMq.matches && !(shortLandscapeMq && shortLandscapeMq.matches));
      var headerBottom = 0;
      if (mobile && siteHeader) {
        if (headerPositionCache === null) headerPositionCache = window.getComputedStyle(siteHeader).position;
        if (headerPositionCache !== 'static') {
          headerBottom = Math.max(0, Math.ceil(siteHeader.getBoundingClientRect().bottom));
        }
      }
      if (headerBottom !== lastStickyTop) {
        lastStickyTop = headerBottom;
        document.documentElement.style.setProperty('--lm-sticky-top', headerBottom + 'px');
      }
      var stickyTop = headerBottom;
      var minimized = main.classList.contains('lm-stage--minimized');
      var away = printAway();
      main.classList.toggle('lm-stage--print-away', away);
      var animating = main.classList.contains('lm-stage--anim');

      // Measure the one layout, only while it is the real, full panel.
      var geomChanged = false;
      if (geomDirty && stacked && !minimized && !away && !animating) {
        var before = geom ? geom.D + ',' + geom.bottom0 + ',' + geom.H : '';
        measureCollapse();
        geomChanged = (geom ? geom.D + ',' + geom.bottom0 + ',' + geom.H : '') !== before;
      }
      var D = (stacked && geom) ? geom.D : 0;
      if (D !== lastCollapseD) {
        lastCollapseD = D;
        main.style.setProperty('--lm-collapse', D + 'px');
        if (roomRelayout) roomRelayout();
      }
      // The map strip down the right edge fits between the cart (top 54px)
      // and the bottom row (48px above the print's bottom edge): seven
      // buttons and six 5px gaps, at most 40px each.
      if (stacked && geom) {
        var avail = geom.H - 54 - geom.bottom0 - 48;
        var btn = Math.max(26, Math.min(40, Math.floor((avail - 30) / 7)));
        if (btn !== lastStripBtn) {
          lastStripBtn = btn;
          main.style.setProperty('--lm-strip-btn', btn + 'px');
        }
      }
      var bottom0 = (stacked && geom) ? geom.bottom0 : null;
      var frameBottom = main.classList.contains('lm-stage--room') ? 24 : bottom0;
      if (frameBottom !== null && frameBottom !== lastFrameBottom) {
        lastFrameBottom = frameBottom;
        main.style.setProperty('--lm-frame-bottom', frameBottom + 'px');
      }

      var mainRect = main.getBoundingClientRect();
      if (roomCheck) roomCheck();
      var p = 0;
      if (stacked && !minimized && !away && D > 0) {
        p = Math.min(1, Math.max(0, (stickyTop - mainRect.top) / D));
      }
      // Typing in the icon card: hold the preview full size (see cardTyping).
      if (cardTyping) p = 0;
      var wasStuck = main.classList.contains('lm-stage--stuck');
      var stuck;
      if (minimized || away) {
        // Pinned at the top edge whole; "stuck" once the page has scrolled
        // it there. Hysteresis stops a flicker on the exact line.
        var scrolled = (window.pageYOffset || document.documentElement.scrollTop || 0) > (wasStuck ? 0 : 8);
        stuck = stacked && scrolled && mainRect.top <= stickyTop + (wasStuck ? 8 : 1);
      } else {
        stuck = stacked && p >= (wasStuck ? 0.9 : 0.98);
      }
      if (stuck !== wasStuck) main.classList.toggle('lm-stage--stuck', stuck);
      // Shrunk (or minimised): the map is a thumbnail, its controls fade and
      // gestures pause until it is full size again.
      var shrunk = stacked && !away && (minimized || p > 0.01);
      if (shrunk !== main.classList.contains('lm-stage--shrunk')) {
        main.classList.toggle('lm-stage--shrunk', shrunk);
        stageLock('shrunk', shrunk);
      }
      if (stacked) paintNotchBand(minimized, mainRect, p);

      var force = forceNextApply || geomChanged;
      forceNextApply = false;
      applyCollapse(stacked ? p : 0, force);

      if (!stuck && minimized) paintToggle(false);
      if ((headerPositionCache && headerPositionCache !== 'static' && Date.now() < headerTrackUntil) || animating) {
        queueStickyPaint(false);
      }
    }

    // Run the customer-initiated toggles with transitions on, then take them
    // off again so no scroll-driven change can inherit them.
    // Scroll anchoring is off for the length of the toggle: Chromium would
    // otherwise hold the controls still by scrolling the page up as the panel
    // shrinks, landing at the top, which reads as "back at the top" and
    // expands the preview again at once. (WebKit has no scroll anchoring.)
    var rootStyle = document.documentElement.style;
    function withAnim(fn) {
      if (animTimer) window.clearTimeout(animTimer);
      main.classList.add('lm-stage--anim');
      rootStyle.overflowAnchor = 'none';
      fn();
      queueStickyPaint(false);
      animTimer = window.setTimeout(function () {
        animTimer = null;
        rootStyle.overflowAnchor = '';
        main.classList.remove('lm-stage--anim');
        geomDirty = true;
        queueStickyPaint(false, true);
      }, 320);
    }

    paintToggle(false);

    // A tap on the shrunk print brings it back to full size (scroll up to
    // where the collapse starts), so the map can be moved again.
    if (frameWrap) {
      frameWrap.addEventListener('click', function (e) {
        if (!main.classList.contains('lm-stage--shrunk') || main.classList.contains('lm-stage--minimized')) return;
        e.preventDefault();
        e.stopPropagation();
        // The collapse starts where the panel's own top (in the flow, before
        // it sticks) meets the sticky line. The panel leads .lm-wrapper on
        // phones, so its flow position is the wrapper's content top.
        var par = main.parentElement;
        var sy = window.pageYOffset || document.documentElement.scrollTop || 0;
        var docTop = par ? par.getBoundingClientRect().top + sy + (parseFloat(getComputedStyle(par).paddingTop) || 0) : 0;
        var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(0, docTop - (lastStickyTop || 0)), behavior: reduceMotion ? 'auto' : 'smooth' });
      }, true);
    }

    toggle.addEventListener('click', function () {
      var minimize = !main.classList.contains('lm-stage--minimized');
      withAnim(function () {
        if (minimize) {
          // Lock the expanded pixel height before toggling the class so WebKit
          // can interpolate from it rather than snapping between auto heights.
          main.style.setProperty('--lm-preview-expanded-height', main.getBoundingClientRect().height + 'px');
          main.getBoundingClientRect();
        }
        paintToggle(minimize);
      });
      queueStickyPaint(false);
    });
    window.addEventListener('scroll', function () { queueStickyPaint(true); }, { passive: true });
    // The icon card (#lm-icon-card) overlays the print inside this panel. A
    // tap on its label field makes iOS scroll the page to clear the keyboard,
    // which collapsed the preview, and the shrunk state fades the card out:
    // the customer was left typing into an invisible field. While a field in
    // the card has focus the collapse holds at full size; on leaving it the
    // page goes back to where it was before the keyboard pushed it.
    var cardTyping = false;
    var cardTypingScrollY = 0;
    function isCardField(el) {
      return !!(el && el.closest && el.closest('#lm-icon-card') &&
        (el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && !/^(button|checkbox|radio|range|file|submit)$/i.test(el.type || ''))));
    }
    document.addEventListener('focusin', function (e) {
      if (!isCardField(e.target) || !mobileNow()) return;
      if (!cardTyping) cardTypingScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      cardTyping = true;
      queueStickyPaint(false, true);
    });
    document.addEventListener('focusout', function (e) {
      if (!cardTyping || !isCardField(e.target)) return;
      // Moving straight to another field in the card keeps the hold.
      if (isCardField(e.relatedTarget)) return;
      cardTyping = false;
      var y = cardTypingScrollY;
      // After the keyboard starts closing, so iOS doesn't undo the restore.
      window.setTimeout(function () {
        if (cardTyping) return;
        if (Math.abs((window.pageYOffset || document.documentElement.scrollTop || 0) - y) > 2) {
          window.scrollTo({ top: y, behavior: 'auto' });
        }
        queueStickyPaint(false, true);
      }, 60);
      queueStickyPaint(false, true);
    });
    window.addEventListener('resize', function () {
      headerPositionCache = null;
      // Keep lastStickyTop / lastFrameBottom: the next paint re-measures and
      // compares. Clearing them rewrote --lm-sticky-top on <html> with the
      // same value on every resize, and iOS fires one per toolbar show/hide
      // mid-scroll; a root custom-property write restyles the whole document
      // (~350ms here), which held the scroll-linked notch band back.
      geomDirty = true;
      queueStickyPaint(true, true);
    });
    // A new size / finish resizes the print; minimise resizes the panel.
    // Transforms don't fire this, so the collapse itself never does.
    if (window.ResizeObserver) {
      var collapseRo = new ResizeObserver(function () {
        geomDirty = true;
        queueStickyPaint(false, true);
      });
      collapseRo.observe(main);
      if (frameWrap) collapseRo.observe(frameWrap);
    }

    // starmap-gallery.js toggles .sm-gallery-viewing on the panel, Room view
    // .lm-stage--room, and the 3D view hides #print. Re-measure the moment
    // the frame comes back.
    if (window.MutationObserver) {
      var sig = function () {
        return [main.classList.contains('sm-gallery-viewing'), main.classList.contains('lm-stage--room'), threeDActive()].join();
      };
      var lastSig = sig();
      var onMut = function () {
        var s = sig();
        if (s === lastSig) return;
        lastSig = s;
        geomDirty = true;
        queueStickyPaint(false, true);
      };
      new MutationObserver(onMut).observe(main, { attributes: true, attributeFilter: ['class'] });
      if (frameWrap) new MutationObserver(onMut).observe(frameWrap, { attributes: true, attributeFilter: ['class'] });
    }

    queueStickyPaint(true);
  }

  // The tools flyout (locationmap.js bindToolbarFab) is anchored to the print's bottom edge,
  // which the gallery viewer and the minimised bar both take away.
  function bindToolsExtras(main) {
    var fab = document.getElementById('lm-toolbar-fab');
    if (!fab || !window.MutationObserver) return;
    new MutationObserver(function () {
      if (!main.classList.contains('lm-tools-open')) return;
      if (main.classList.contains('sm-gallery-viewing') || main.classList.contains('lm-stage--minimized')) fab.click();
    }).observe(main, { attributes: true, attributeFilter: ['class'] });
    var theme = document.getElementById('lm-toolbar-theme');
    if (theme) theme.addEventListener('click', function () {
      // Theme is a one-shot switch like Info and Save: close the flyout.
      if (main.classList.contains('lm-tools-open')) fab.click();
    });
  }

  // ── Room view ───────────────────────────────────────────────────────────
  // Lays the live print onto the blank poster in a photographed scene
  // (#lm-room-scenes in the section: image, poster corners, months). The
  // print is the real #print, transformed with a matrix3d, so every edit
  // shows up in the room immediately and the text stays exact.
  //
  // Geometry: the poster is a 5:7 sheet (7:5 in a scene's "land" variant, for
  // landscape prints). The print keeps its own aspect and is sized to COVER
  // the sheet, so no blank paper ever peeks out and nothing is stretched.
  var SHEET_ASPECT = 5 / 7;

  function homography(src, dst) {
    // Solve the 8 unknowns of H (h33 = 1) mapping 4 src points onto 4 dst.
    var A = [], b = [];
    for (var i = 0; i < 4; i++) {
      var x = src[i][0], y = src[i][1], u = dst[i][0], v = dst[i][1];
      A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
      A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
    }
    for (var c = 0; c < 8; c++) {
      var piv = c;
      for (var r = c + 1; r < 8; r++) if (Math.abs(A[r][c]) > Math.abs(A[piv][c])) piv = r;
      var tA = A[c]; A[c] = A[piv]; A[piv] = tA;
      var tb = b[c]; b[c] = b[piv]; b[piv] = tb;
      for (var r2 = c + 1; r2 < 8; r2++) {
        var f = A[r2][c] / A[c][c];
        for (var k = c; k < 8; k++) A[r2][k] -= f * A[c][k];
        b[r2] -= f * b[c];
      }
    }
    var h = new Array(8);
    for (var r3 = 7; r3 >= 0; r3--) {
      var sum = b[r3];
      for (var k2 = r3 + 1; k2 < 8; k2++) sum -= A[r3][k2] * h[k2];
      h[r3] = sum / A[r3][r3];
    }
    return [[h[0], h[1], h[2]], [h[3], h[4], h[5]], [h[6], h[7], 1]];
  }
  function applyH(H, x, y) {
    var w = H[2][0] * x + H[2][1] * y + H[2][2];
    return [(H[0][0] * x + H[0][1] * y + H[0][2]) / w, (H[1][0] * x + H[1][1] * y + H[1][2]) / w];
  }
  function matrix3d(H) {
    return 'matrix3d(' + [
      H[0][0], H[1][0], 0, H[2][0],
      H[0][1], H[1][1], 0, H[2][1],
      0, 0, 1, 0,
      H[0][2], H[1][2], 0, H[2][2]
    ].map(function (n) { return +n.toFixed(8); }).join(',') + ')';
  }

  function bindRoomView(main) {
    var wrap = document.getElementById('lm-print-frame-wrap');
    var bg = document.getElementById('lm-room-bg');
    var light = document.getElementById('lm-room-light');
    var dataEl = document.getElementById('lm-room-scenes');
    var toggles = document.querySelectorAll('[data-lm-room-toggle]');
    if (!wrap || !bg || !light || !dataEl || !toggles.length) return;
    var scenes;
    try { scenes = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!scenes || !scenes.length) return;

    // ?room=<id> previews a scene; otherwise the one in season.
    var scene = null;
    try {
      var want = new URLSearchParams(window.location.search).get('room');
      if (want) scene = scenes.filter(function (s) { return s.id === want; })[0] || null;
    } catch (e) { /* noop */ }
    if (!scene) {
      var month = new Date().getMonth() + 1;
      scene = scenes.filter(function (s) { return (s.months || []).indexOf(month) !== -1; })[0] || scenes[0];
    }

    var on = false, raf = 0;
    var roomEl = document.getElementById('lm-room') || bg.parentNode;
    var backdrop = null;

    // Which photo of the scene is showing: the portrait original, or its wide
    // landscape outpaint (scene.wide) on desktop-shaped panels, whichever
    // aspect is closer to the panel's. A change waits until the new photo is
    // decoded, then swaps and re-lays out in the same frame.
    var view = scene, viewSwitching = false;
    // A landscape print lies on the scene's landscape poster ("land"), when
    // the scene has one; then portrait photo or wide outpaint by the panel's
    // aspect, as ever.
    function printLandscape() {
      var f = document.getElementById('lm-print-frame');
      return !!(f && f.classList.contains('landscape'));
    }
    function pickView(W, H) {
      var base = (printLandscape() && scene.land) ? scene.land : scene;
      if (!base.wide || !W || !H) return base;
      var a = W / H;
      return Math.abs(Math.log(a * base.wide.h / base.wide.w)) < Math.abs(Math.log(a * base.h / base.w)) ? base.wide : base;
    }
    function sheetAspect(v) { return v.sheet || (v === scene.land || (scene.land && v === scene.land.wide) ? 7 / 5 : SHEET_ASPECT); }
    function useView(v) {
      view = v;
      bg.src = v.src;
      light.src = v.light || v.src;
    }

    // Cover the panel, centred on the poster, never showing past the photo's
    // edges; but never let the poster grow past POSTER_MAX of the panel
    // height (on a wide desktop panel "cover" alone blew the print up past
    // the panel). When the cap wins, the photo stops short of the panel on
    // that axis and stays centred on the poster.
    var POSTER_MAX = 0.8;
    function roomFit(W, H) {
      var q = view.quad;
      var qTop = Math.min(q[0][1], q[1][1], q[2][1], q[3][1]);
      var qBot = Math.max(q[0][1], q[1][1], q[2][1], q[3][1]);
      var qcx = (q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4;
      var qcy = (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4;
      var sCover = Math.max(W / view.w, H / view.h);
      var sCap = POSTER_MAX * H / Math.max(1, qBot - qTop);
      var sc = Math.min(sCover, sCap);
      var iw = view.w * sc, ih = view.h * sc;
      var left = iw >= W ? Math.min(0, Math.max(W - iw, W / 2 - qcx * sc)) : W / 2 - qcx * sc;
      var top = ih >= H ? Math.min(0, Math.max(H - ih, H / 2 - qcy * sc)) : H / 2 - qcy * sc;
      return { s: sc, left: left, top: top };
    }

    function schedule() { if (on && !raf) raf = requestAnimationFrame(layout); }

    function layout() {
      raf = 0;
      if (!on) return;
      var W = main.clientWidth, Hc = main.clientHeight;
      var fw = wrap.offsetWidth, fh = wrap.offsetHeight;
      if (!W || !Hc || !fw || !fh) return;

      var wantView = pickView(W, Hc);
      if (wantView !== view && !viewSwitching) {
        viewSwitching = true;
        var pre = new Image();
        pre.src = wantView.src;
        (pre.decode ? pre.decode() : Promise.resolve()).catch(function () {}).then(function () {
          viewSwitching = false;
          if (!on || pickView(main.clientWidth, main.clientHeight) !== wantView) { schedule(); return; }
          useView(wantView);
          layout();
        });
      }
      var q = view.quad;
      var fit = roomFit(W, Hc);
      var s = fit.s, left = fit.left, top = fit.top;
      var iw = view.w * s, ih = view.h * s;
      [bg, light].forEach(function (img) {
        img.style.width = iw + 'px'; img.style.height = ih + 'px';
        img.style.left = left + 'px'; img.style.top = top + 'px';
      });
      // Where the photo stops short of the panel (wide desktop panels), a
      // blurred copy of the scene fills the rest and the photo's edges fade
      // into it, so it reads as one room rather than a picture in a box.
      var gapX = iw < W - 0.5, gapY = ih < Hc - 0.5;
      roomEl.classList.toggle('is-contained', gapX || gapY);
      var fade = [];
      if (gapX) fade.push('linear-gradient(to right, transparent 0, #000 9%, #000 91%, transparent 100%)');
      if (gapY) fade.push('linear-gradient(to bottom, transparent 0, #000 9%, #000 91%, transparent 100%)');
      var mask = fade.length ? fade.join(', ') : '';
      bg.style.webkitMaskImage = bg.style.maskImage = mask;
      bg.style.webkitMaskComposite = fade.length > 1 ? 'source-in' : '';
      bg.style.maskComposite = fade.length > 1 ? 'intersect' : '';
      if (gapX || gapY) {
        if (!backdrop) {
          backdrop = document.createElement('div');
          backdrop.className = 'lm-room__backdrop';
          roomEl.insertBefore(backdrop, roomEl.firstChild);
        }
        var src = 'url("' + (bg.currentSrc || bg.src) + '")';
        if (backdrop.style.backgroundImage !== src) backdrop.style.backgroundImage = src;
      }

      // The PRINT goes on the sheet: #lm-print-frame's content box (a Framed
      // finish's moulding is its border, so it extends past the sheet as a
      // real frame around a print that size would).
      var frame = wrap.querySelector('#lm-print-frame');
      var px0 = 0, py0 = 0, pWidth = fw, pHeight = fh;
      if (frame && frame.clientWidth && frame.clientHeight) {
        px0 = frame.offsetLeft + frame.clientLeft;
        py0 = frame.offsetTop + frame.clientTop;
        pWidth = frame.clientWidth;
        pHeight = frame.clientHeight;
      }

      // Print rectangle in sheet units (sheet = SHEET_ASPECT x 1), covering it.
      var ap = pWidth / pHeight;
      var SA = sheetAspect(view);
      var ph = Math.max(1, SA / ap) * 1.012;
      var pw = ph * ap;
      var u0 = (SA - pw) / 2 / SA, u1 = (SA + pw) / 2 / SA;
      var v0 = (1 - ph) / 2, v1 = (1 + ph) / 2;
      var sheet = homography([[0, 0], [1, 0], [1, 1], [0, 1]], q);
      var corners = [[u0, v0], [u1, v0], [u1, v1], [u0, v1]].map(function (c) {
        var p = applyH(sheet, c[0], c[1]);
        return [p[0] * s + left, p[1] * s + top];   // panel coordinates
      });

      // #print keeps its layout box; map that box onto the corners.
      var ox = wrap.offsetLeft, oy = wrap.offsetTop;
      placedAt = placementKey();
      var M = homography(
        [[px0, py0], [px0 + pWidth, py0], [px0 + pWidth, py0 + pHeight], [px0, py0 + pHeight]],
        corners.map(function (c) { return [c[0] - ox, c[1] - oy]; }));
      wrap.style.setProperty('transform-origin', '0 0', 'important');
      wrap.style.setProperty('transform', matrix3d(M), 'important');

      // Lamplight across the whole object: the print, a Framed moulding and
      // any gift wrap / ribbon. The light map is a smooth field fitted to the
      // blank sheet, so extending it past the sheet adds no floor texture.
      var ring = Math.max(2, frame ? frame.clientLeft : 0);
      var outer = [[px0 - ring, py0 - ring], [px0 + pWidth + ring, py0 - ring],
        [px0 + pWidth + ring, py0 + pHeight + ring], [px0 - ring, py0 + pHeight + ring]].map(function (c) {
        var qq = applyH(M, c[0], c[1]);
        return [qq[0] + ox, qq[1] + oy];
      });
      // Whole pixels: a fractional polygon makes Chromium draw a hairline
      // seam along the multiplied layer's bounding box.
      light.style.clipPath = 'polygon(' + outer.map(function (c) {
        return Math.round(c[0] - left) + 'px ' + Math.round(c[1] - top) + 'px';
      }).join(', ') + ')';

      // Where the scene ends up once the phone preview has collapsed: the
      // same cover fit, into the D px shorter strip still showing. As one
      // similarity P -> kP + t on the panel it moves photo, light map and
      // print together; linear steps between two covering fits always cover.
      var D = getCollapseD ? getCollapseD() : 0;
      var Hc1 = Math.max(1, Hc - D);
      var fit1 = roomFit(W, Hc1);
      var s1 = fit1.s, left1 = fit1.left, top1 = fit1.top;
      var kR = s1 / s;
      bg.style.transformOrigin = light.style.transformOrigin = '0 0';
      sampleEdgeColor(s1, left1, top1, W);
      roomCollapse = {
        key: [W, Hc, D, left, top, s, ox, oy].map(function (n) { return Math.round(n * 100); }).join(','),
        end: { k: kR, tx: left1 - kR * left, ty: D + top1 - kR * top },
        items: [[bg, left, top], [light, left, top], [wrap, ox, oy]],
        photo: { src: bg.currentSrc || bg.src, w: iw, h: ih, left: left, top: top }
      };
      if (onRoomLayout) onRoomLayout();
    }
    roomRelayout = schedule;

    // The print is pinned to the photo by its layout position in the panel.
    // Resize, size changes and panel classes re-map it, but the print can also
    // move without any of those firing (iOS applying the layout late after a
    // rotation, the panel's contents shifting): it then sits off its sheet in
    // the photo. Compare on every sticky paint and after resizes settle.
    var placedAt = null;
    function placementKey() {
      var fr = wrap.querySelector('#lm-print-frame');
      return [main.clientWidth, main.clientHeight, wrap.offsetLeft, wrap.offsetTop, wrap.offsetWidth, wrap.offsetHeight,
        fr ? fr.offsetLeft + fr.clientLeft : 0, fr ? fr.offsetTop + fr.clientTop : 0].join(',');
    }
    roomCheck = function () {
      if (!on || raf) return;
      if (placementKey() !== placedAt) schedule();
    };
    var settleTimers = [];
    function settleChecks() {
      settleTimers.forEach(clearTimeout);
      settleTimers = [250, 700, 1500].map(function (ms) { return setTimeout(function () { if (roomCheck) roomCheck(); }, ms); });
    }
    window.addEventListener('resize', settleChecks);
    window.addEventListener('orientationchange', settleChecks);
    window.addEventListener('pageshow', settleChecks);

    // What sits above the viewport edge once the preview has collapsed, as a
    // left-to-right gradient of the photo's colours there: the notch band
    // wears it in Room view. Theme assets are same-origin, so the canvas read
    // is allowed; if not, the CSS fallback stays.
    var EDGE_COLUMNS = 12, EDGE_SPAN = 16;
    var edgeKey = null, edgeCanvas = null;
    function sampleEdgeColor(s1, left1, top1, W) {
      var key = [view.src, Math.round(s1 * 1e4), Math.round(left1), Math.round(top1), W].join(',');
      if (key === edgeKey || !bg.naturalWidth) return;
      edgeKey = key;
      try {
        var sx = Math.max(0, -left1 / s1);
        var sy1 = Math.max(0, -top1 / s1);
        var sy0 = Math.max(0, sy1 - EDGE_SPAN / s1);
        if (sy1 - sy0 < 1) sy1 = Math.min(view.h, sy0 + EDGE_SPAN / s1);
        var sw = Math.min(view.w - sx, W / s1);
        if (sw <= 0) return;
        var r = bg.naturalWidth / view.w;
        var CW = 8, CH = 4, cw = EDGE_COLUMNS * CW;
        edgeCanvas = edgeCanvas || document.createElement('canvas');
        edgeCanvas.width = cw; edgeCanvas.height = CH;
        var ctx = edgeCanvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bg, sx * r, sy0 * r, sw * r, (sy1 - sy0) * r, 0, 0, cw, CH);
        var d = ctx.getImageData(0, 0, cw, CH).data, stops = [], avg = [0, 0, 0];
        for (var c = 0; c < EDGE_COLUMNS; c++) {
          var sum = [0, 0, 0];
          for (var yy = 0; yy < CH; yy++) {
            for (var xx = c * CW; xx < (c + 1) * CW; xx++) {
              var o = (yy * cw + xx) * 4;
              sum[0] += d[o]; sum[1] += d[o + 1]; sum[2] += d[o + 2];
            }
          }
          var col = sum.map(function (v) { return Math.round(v / (CW * CH)); });
          stops.push('rgb(' + col.join(',') + ') ' + ((c + 0.5) / EDGE_COLUMNS * 100).toFixed(1) + '%');
          avg[0] += col[0]; avg[1] += col[1]; avg[2] += col[2];
        }
        var band = document.querySelector('body > .lm-notch-band');
        if (!band) return;
        band.style.setProperty('--lm-edge-color', 'rgb(' + avg.map(function (v) { return Math.round(v / EDGE_COLUMNS); }).join(',') + ')');
        band.style.setProperty('--lm-edge-fill', 'linear-gradient(to right, ' + stops.join(', ') + ')');
      } catch (e) { /* tainted or unavailable: keep the CSS fallback */ }
    }

    function paint() {
      Array.prototype.forEach.call(toggles, function (t) {
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
        t.setAttribute('aria-label', on ? 'Switch to the flat preview' : 'See it in a room');
        t.title = on ? 'Flat preview' : 'See it in a room';
      });
    }

    // ── On / off, with a crossfade ───────────────────────────────────────
    // On request only (the Room button). The map is the thing customers edit
    // here, so the stage opens on the flat, live map; Room view is a look,
    // and it isn't remembered (nor does it touch the star map / moon
    // preference, 'skylit-room-view').
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var sceneReady = null, fadeTimer = 0;

    function loadScene() {
      if (sceneReady) return sceneReady;
      view = pickView(main.clientWidth, main.clientHeight);
      sceneReady = Promise.all([[bg, view.src], [light, view.light || view.src]].map(function (pair) {
        return new Promise(function (resolve) {
          var img = pair[0];
          img.onload = img.onerror = function () { resolve(); };
          img.src = pair[1];
          if (img.complete && img.naturalWidth) resolve();
        });
      }));
      return sceneReady;
    }

    // The live map can't go into the room itself: on phones it renders at 3x
    // (supersampled, then scaled down), and a WebGL canvas that size under a
    // 3D transform is only partly drawn. The room shows a snapshot of it at
    // the slot's own size instead, refreshed whenever the map settles (a
    // theme picked from the sidebar while the room is up). Icons and text
    // are DOM and stay live.
    var snap = null;
    function paintSnap() {
      if (!on) return;
      var c = document.querySelector('#lm-map .mapboxgl-canvas');
      var inner = document.getElementById('lm-map-inner');
      if (!c || !inner || !c.width) return;
      var w = inner.clientWidth, h = inner.clientHeight;
      if (!w || !h) return;
      var dpr = Math.min(2, window.devicePixelRatio || 1);
      if (!snap) {
        snap = document.createElement('canvas');
        snap.className = 'lm-room-snap';
        snap.setAttribute('aria-hidden', 'true');
      }
      if (snap.parentNode !== inner) inner.insertBefore(snap, inner.firstChild);
      snap.width = Math.round(w * dpr);
      snap.height = Math.round(h * dpr);
      try { copyMapCanvas(c, snap.getContext('2d'), snap.width, snap.height); } catch (e) { /* noop */ }
    }
    // While the sidebar's sliders or the location search move the map under
    // the room, repaint from its render event too (the drawing buffer is
    // only readable inside the frame that drew it), a few times a second.
    var lastLivePaint = 0;
    function paintSnapLive() {
      var now = Date.now();
      // Every frame while a gesture in the room is moving the map.
      if (!g && now - lastLivePaint < 120) return;
      lastLivePaint = now;
      paintSnap();
    }
    var snapMap = null;
    function watchSnap(watch) {
      var m = window.__lmMapInst;
      if (snapMap && (snapMap !== m || !watch)) {
        try { snapMap.off('idle', paintSnap); snapMap.off('render', paintSnapLive); } catch (e) {}
        snapMap = null;
      }
      if (watch && m && !snapMap) { snapMap = m; m.on('idle', paintSnap); m.on('render', paintSnapLive); }
    }

    function apply(next) {
      on = next;
      if (on) paintSnap();
      watchSnap(on);
      main.classList.toggle('lm-stage--snap', on);
      // Mapbox can't track a gesture in perspective: its handlers pause
      // while the room is up and the gestures are taken here (below).
      stageLock('room', on);
      if (on) {
        main.classList.add('lm-stage--room');
        layout();
      } else {
        main.classList.remove('lm-stage--room');
        wrap.style.removeProperty('transform');
        wrap.style.removeProperty('transform-origin');
        light.style.clipPath = '';
        if (onRoomLayout) onRoomLayout();
      }
      paint();
      // The map re-measures its canvas against the new box.
      if (window.lmMapResize) window.lmMapResize();
    }

    function crossfade(fn) {
      clearTimeout(fadeTimer);
      if (reduce) { fn(); return; }
      main.classList.add('lm-stage--fading');
      fadeTimer = setTimeout(function () {
        fn();
        main.classList.add('lm-stage--room-enter');
        void main.offsetWidth;
        main.classList.remove('lm-stage--fading');
        fadeTimer = setTimeout(function () { main.classList.remove('lm-stage--room-enter'); }, 480);
      }, 190);
    }

    function set(next) {
      if (next) {
        loadScene().then(function () {
          if (!on) crossfade(function () { apply(true); });
        });
      } else if (on) {
        crossfade(function () { apply(false); });
      }
    }

    Array.prototype.forEach.call(toggles, function (t) {
      t.addEventListener('click', function () { set(!on); });
    });

    // ── Editing in the room ──────────────────────────────────────────────
    // Mapbox reads every gesture against the container's on-screen box,
    // which under the room's matrix3d lands in the wrong place, so its own
    // handlers stay off in the room (stageLock above) and the gestures are
    // taken here instead: pan (one finger / left button), pinch to zoom and
    // twist to rotate (two fingers), two fingers up / down to tilt, right
    // button (or ctrl + drag) to rotate and tilt, the wheel to zoom, and
    // icons drag as they do flat. Four tiny probes at the map container's
    // corners give, through the browser's own layout (supersample scale,
    // matrix3d, the phone collapse, everything), where those corners are on
    // screen; the homography back from screen to container pixels turns a
    // finger into a map point. The map controls and the sidebar work as
    // ever; the snapshot follows the map.
    var probes = null;
    function screenToContainer(m) {
      var c = m.getContainer();
      var cw = c.clientWidth, ch = c.clientHeight;
      if (!cw || !ch) return null;
      if (!probes || probes[0].parentNode !== c) {
        probes = [0, 1, 2, 3].map(function () {
          var p = document.createElement('div');
          p.setAttribute('aria-hidden', 'true');
          // display set outright: base.css hides an empty div (div:empty).
          p.style.cssText = 'display:block!important;position:absolute;width:1px;height:1px;margin:-0.5px 0 0 -0.5px;opacity:0;pointer-events:none;';
          c.appendChild(p);
          return p;
        });
      }
      var local = [[0, 0], [cw, 0], [cw, ch], [0, ch]];
      var screen = probes.map(function (p, i) {
        p.style.left = local[i][0] + 'px'; p.style.top = local[i][1] + 'px';
        var r = p.getBoundingClientRect();
        return [(r.left + r.right) / 2, (r.top + r.bottom) / 2];
      });
      return homography(screen, local);
    }
    function userLocked() { return typeof window.lmUserLocked === 'function' && window.lmUserLocked(); }
    function merc(ll) { return window.mapboxgl.MercatorCoordinate.fromLngLat(ll); }
    // Move the camera so the map point `ll` sits under container pixel `pt`.
    function anchorAt(m, ll, pt) {
      var c = m.getContainer();
      var x = Math.max(0, Math.min(c.clientWidth, pt[0])), y = Math.max(0, Math.min(c.clientHeight, pt[1]));
      var want = merc(ll), have = merc(m.unproject([x, y])), ctr = merc(m.getCenter());
      var next = new window.mapboxgl.MercatorCoordinate(ctr.x + want.x - have.x, ctr.y + want.y - have.y);
      var to = next.toLngLat();
      if (isFinite(to.lng) && isFinite(to.lat)) m.jumpTo({ center: to });
    }
    // A grab near the horizon of a tilted map would fly the camera away (see
    // lmGrabIsDegenerate in locationmap.js): measured here in container px.
    function grabTooFar(m, pt) {
      try {
        var c = m.getContainer(), ctr = m.getCenter();
        var near = merc(m.unproject([c.clientWidth / 2, c.clientHeight - 1])), mid = merc(ctr), at = merc(m.unproject(pt));
        var span = Math.hypot(near.x - mid.x, near.y - mid.y);
        return span > 0 && Math.hypot(at.x - mid.x, at.y - mid.y) > 3 * span;
      } catch (e) { return false; }
    }

    var g = null;            // the gesture in progress
    var pts = {};            // pointerId -> [clientX, clientY]
    var frameQueued = false;
    function ptsList() { return Object.keys(pts).map(function (k) { return pts[k]; }); }

    function roomPress(e) {
      var m = window.__lmMapInst;
      if (!m || !window.mapboxgl) return false;
      // Shrunk phone preview: a tap scrolls back up (bindMobilePreview).
      if (main.classList.contains('lm-stage--shrunk')) return true;
      // A button on an icon (the sample pin's close) works as a tap.
      var marker = e.target.closest('.mapboxgl-marker');
      var btn = e.target.closest('button, [role="button"]');
      if (btn && btn !== marker) return true;
      var H = screenToContainer(m);
      if (!H) return true;
      e.preventDefault();
      e.stopPropagation();
      pts[e.pointerId] = [e.clientX, e.clientY];
      var n = Object.keys(pts).length;
      if (n === 1 && marker && marker.__marker && !e.button) {
        var mk = marker.__marker, at = applyH(H, e.clientX, e.clientY), pin = m.project(mk.getLngLat());
        g = { kind: 'icon', m: m, H: H, marker: mk, moved: false, x0: e.clientX, y0: e.clientY, dx: pin.x - at[0], dy: pin.y - at[1] };
        return true;
      }
      if (userLocked()) { g = null; return true; }
      if (g && g.kind === 'icon') { if (g.moved) g.marker.fire('dragend'); g = null; }
      if (n === 1) {
        var p = applyH(H, e.clientX, e.clientY);
        if (e.button === 2 || e.ctrlKey) {
          g = { kind: 'turn', m: m, H: H, last: [e.clientX, e.clientY] };
        } else if (grabTooFar(m, p)) {
          g = null;
        } else {
          g = { kind: 'pan', m: m, H: H, ll: m.unproject(p) };
        }
      } else if (n === 2) {
        startPinch(m, H);
      }
      return true;
    }
    function startPinch(m, H) {
      var l = ptsList();
      var a = applyH(H, l[0][0], l[0][1]), b = applyH(H, l[1][0], l[1][1]);
      var mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      g = {
        kind: 'pinch', m: m, H: H, mode: null,
        ll: m.unproject(mid), dist: Math.hypot(b[0] - a[0], b[1] - a[1]), ang: Math.atan2(b[1] - a[1], b[0] - a[0]),
        zoom0: m.getZoom(), bearing0: m.getBearing(), pitch0: m.getPitch(),
        s0: [l[0].slice(), l[1].slice()]
      };
    }
    function step() {
      frameQueued = false;
      var d = g;
      if (!d) return;
      var m = d.m, l = ptsList();
      if (d.kind === 'pan' && l.length === 1) {
        anchorAt(m, d.ll, applyH(d.H, l[0][0], l[0][1]));
      } else if (d.kind === 'turn' && l.length === 1) {
        var dx = l[0][0] - d.last[0], dy = l[0][1] - d.last[1];
        d.last = l[0].slice();
        m.jumpTo({ bearing: m.getBearing() - dx * 0.8, pitch: m.getPitch() - dy * 0.5 });
      } else if (d.kind === 'pinch' && l.length === 2) {
        var a = applyH(d.H, l[0][0], l[0][1]), b = applyH(d.H, l[1][0], l[1][1]);
        var mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
        if (!d.mode) {
          // Two fingers side by side moving up or down together: tilt.
          // Otherwise zoom + rotate around the fingers.
          var d1 = l[0][1] - d.s0[0][1], d2 = l[1][1] - d.s0[1][1];
          var x1 = l[0][0] - d.s0[0][0], x2 = l[1][0] - d.s0[1][0];
          var moved = Math.max(Math.abs(d1), Math.abs(d2), Math.abs(x1), Math.abs(x2));
          if (moved < 6) return;
          var level = Math.abs(d.s0[0][1] - d.s0[1][1]) < Math.abs(d.s0[0][0] - d.s0[1][0]) * 0.6;
          d.mode = (level && d1 * d2 > 0 && Math.abs(d1) > Math.abs(x1) && Math.abs(d2) > Math.abs(x2)) ? 'tilt' : 'zoom';
        }
        if (d.mode === 'tilt') {
          var dy2 = ((l[0][1] - d.s0[0][1]) + (l[1][1] - d.s0[1][1])) / 2;
          m.jumpTo({ pitch: d.pitch0 - dy2 * 0.5 });
        } else {
          var dist = Math.hypot(b[0] - a[0], b[1] - a[1]), ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
          if (!(dist > 0) || !(d.dist > 0)) return;
          m.jumpTo({
            zoom: d.zoom0 + Math.log(dist / d.dist) / Math.LN2,
            bearing: d.bearing0 - (ang - d.ang) * 180 / Math.PI
          });
          anchorAt(m, d.ll, mid);
        }
      }
    }
    function roomMove(e) {
      if (!(e.pointerId in pts)) return;
      pts[e.pointerId] = [e.clientX, e.clientY];
      var d = g;
      if (!d) return;
      e.preventDefault();
      if (d.kind === 'icon') {
        if (!d.moved) {
          if (Math.abs(e.clientX - d.x0) + Math.abs(e.clientY - d.y0) < 4) return;
          d.moved = true;
          d.marker.fire('dragstart');
        }
        var p = applyH(d.H, e.clientX, e.clientY);
        var c = d.m.getContainer();
        var x = Math.max(0, Math.min(c.clientWidth, p[0] + d.dx));
        var y = Math.max(0, Math.min(c.clientHeight, p[1] + d.dy));
        d.marker.setLngLat(d.m.unproject([x, y]));
        d.marker.fire('drag');
        return;
      }
      if (!frameQueued) { frameQueued = true; requestAnimationFrame(step); }
    }
    function roomUp(e) {
      if (!(e.pointerId in pts)) return;
      delete pts[e.pointerId];
      var d = g;
      if (!d) return;
      if (d.kind === 'icon') { g = null; if (d.moved) d.marker.fire('dragend'); return; }
      var l = ptsList();
      // Lifting one finger of a pinch carries on as a pan from the other.
      if (l.length === 1 && d.kind === 'pinch') {
        var H = screenToContainer(d.m);
        g = H ? { kind: 'pan', m: d.m, H: H, ll: d.m.unproject(applyH(H, l[0][0], l[0][1])) } : null;
      } else if (!l.length) {
        g = null;
      }
    }
    window.addEventListener('pointermove', roomMove, { passive: false });
    window.addEventListener('pointerup', roomUp);
    window.addEventListener('pointercancel', roomUp);
    // Keep the page still under fingers working the map, and keep Mapbox's
    // own touch / mouse marker drag from starting alongside ours.
    window.addEventListener('touchmove', function (e) { if (g) e.preventDefault(); }, { passive: false });
    function blockMapbox(e) { if (on) e.stopPropagation(); }

    var mapEl = document.getElementById('lm-map');
    if (mapEl) {
      mapEl.addEventListener('pointerdown', function (e) { if (on) roomPress(e); }, true);
      mapEl.addEventListener('touchstart', blockMapbox, { capture: true, passive: true });
      mapEl.addEventListener('mousedown', blockMapbox, true);
      mapEl.addEventListener('contextmenu', function (e) { if (on) e.preventDefault(); });
      mapEl.addEventListener('wheel', function (e) {
        if (!on || userLocked()) return;
        var m = window.__lmMapInst, H = m && screenToContainer(m);
        if (!H) return;
        e.preventDefault();
        var dy = e.deltaY * (e.deltaMode === 1 ? 40 : 1);
        var ll = m.unproject(applyH(H, e.clientX, e.clientY));
        m.jumpTo({ zoom: m.getZoom() - dy / (e.ctrlKey ? 100 : 450) });
        anchorAt(m, ll, applyH(H, e.clientX, e.clientY));
      }, { passive: false });
    }

    window.addEventListener('resize', schedule);
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(schedule);
      ro.observe(main); ro.observe(wrap);
      var frameEl = wrap.querySelector('#lm-print-frame');
      if (frameEl) ro.observe(frameEl);
    }
    // Stuck/unstuck and minimise change the panel; a finish change swaps the
    // frame's classes (the wood ring widens the light map).
    if (window.MutationObserver) {
      new MutationObserver(schedule).observe(main, { attributes: true, attributeFilter: ['class'] });
      var fr = wrap.querySelector('#lm-print-frame');
      if (fr) new MutationObserver(schedule).observe(fr, { attributes: true, attributeFilter: ['class'] });
    }
    paint();
  }

  // ── Full-screen print viewer ────────────────────────────────────────────
  // Any [data-lm-viewer-open] button opens #lm-viewer with a snapshot of
  // the live print. The clone keeps the on-page pixel size and is scaled with
  // a transform (fit x zoom) instead of being re-laid out at viewer size: the
  // caption runs on cqh/cqw and the wood ring is a fixed 22px, so scaling is
  // the only way every element keeps its exact proportion to the print.
  // "100%" = the whole print fitted to the stage.
  function bindPrintViewer() {
    var viewer = document.getElementById('lm-viewer');
    var stage = document.getElementById('lm-viewer-stage');
    var canvas = document.getElementById('lm-viewer-canvas');
    var level = document.getElementById('lm-viewer-level');
    var fsBtn = document.getElementById('lm-viewer-fs');
    var hint = document.getElementById('lm-viewer-hint');
    var openers = document.querySelectorAll('[data-lm-viewer-open]');
    if (!viewer || !stage || !canvas || !openers.length) return;

    // Park the dialog on <body>: inside the section it would inherit the
    // stacking context of the sticky preview on mobile.
    if (viewer.parentNode !== document.body) document.body.appendChild(viewer);

    var MIN = 1, MAX = 5;
    var STEPS = [1, 1.5, 2, 3, 4, 5];
    var baseW = 0, baseH = 0, fit = 1;
    var zoom = 1, tx = 0, ty = 0;
    var returnFocus = null;
    var prevOverflow = '';
    var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

    if (hint) hint.textContent = coarse ? 'Pinch to zoom, swipe down to close.' : 'Zoom in, then drag to explore.';

    function fsElement() { return document.fullscreenElement || document.webkitFullscreenElement || null; }
    var fsSupported = !!(viewer.requestFullscreen || viewer.webkitRequestFullscreen) &&
      !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);
    if (fsBtn) fsBtn.hidden = !fsSupported;

    // The frame moulding is a 9-slice border-image. Scaled up here, the
    // browser paints its corner and edge tiles separately and their
    // antialiased edges show as hairlines at the mitres. Paint the same nine
    // slices into one canvas laid over the box instead: one bitmap scales
    // without seams. `cs` is the computed style carrying the border-image,
    // `bw` the border widths [top, right, bottom, left] in CSS px.
    // `onPainted` runs once the canvas holds the frame, so the caller can
    // drop the original border-image without a blank frame in between.
    function paintFrameRing(cs, w, h, bw, onPainted) {
      var src = /url\(\s*["']?([^"')]+)["']?\s*\)/.exec(cs.getPropertyValue('border-image-source') || '');
      var sl = (cs.getPropertyValue('border-image-slice') || '').split(/\s+/).map(parseFloat).filter(function (n) { return n > 0; });
      if (!src || !sl.length || !w || !h || !(bw[0] || bw[1] || bw[2] || bw[3])) return null;
      while (sl.length < 4) sl.push(sl[sl.length === 3 ? 1 : 0]);
      // Native texture resolution (slice px per border px), capped for memory.
      var k = 1;
      for (var i = 0; i < 4; i++) if (bw[i] > 0) k = Math.max(k, sl[i] / bw[i]);
      k = Math.min(k, 4096 / Math.max(w, h));
      var c = document.createElement('canvas');
      c.width = Math.round(w * k);
      c.height = Math.round(h * k);
      c.setAttribute('aria-hidden', 'true');
      c.style.cssText = 'position:absolute;pointer-events:none;z-index:20;width:' + w + 'px;height:' + h + 'px;';
      var img = new Image();
      var draw = function () {
        var iw = img.naturalWidth, ih = img.naturalHeight;
        if (!iw || !ih) return;
        var W = c.width, H = c.height;
        var T = Math.round(bw[0] * k), R = Math.round(bw[1] * k), B = Math.round(bw[2] * k), L = Math.round(bw[3] * k);
        var sx = [0, sl[3], iw - sl[1], iw], sy = [0, sl[0], ih - sl[2], ih];
        var dx = [0, L, W - R, W], dy = [0, T, H - B, H];
        var ctx = c.getContext('2d');
        for (var r = 0; r < 3; r++) {
          for (var q = 0; q < 3; q++) {
            if (r === 1 && q === 1) continue;
            var sw = sx[q + 1] - sx[q], sh = sy[r + 1] - sy[r];
            var dw = dx[q + 1] - dx[q], dh = dy[r + 1] - dy[r];
            if (sw > 0 && sh > 0 && dw > 0 && dh > 0) ctx.drawImage(img, sx[q], sy[r], sw, sh, dx[q], dy[r], dw, dh);
          }
        }
        if (onPainted) onPainted();
      };
      img.onload = draw;
      img.src = src[1];
      if (img.complete && img.naturalWidth) { img.onload = null; draw(); }
      return c;
    }

    // The clone is the print with its frame ring: the .custom-frame box plus
    // the 22px ring when a wood frame is on (it paints outside the box).
    function buildClone() {
      var srcFrame = document.getElementById('lm-print-frame');
      if (!srcFrame) return false;
      // offsetWidth ignores the sticky preview's scale and Room view's
      // matrix3d, which is what we want: the print's real laid-out size.
      var fwPx = srcFrame.offsetWidth, fhPx = srcFrame.offsetHeight;
      if (!fwPx || !fhPx) return false;
      var ring = /\bframe-(black|white|natural|dark-brown)\b/.test(srcFrame.className) ? 22 : 0;
      baseW = fwPx + ring * 2;
      baseH = fhPx + ring * 2;

      var clone = srcFrame.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      // The location map's sample photo pin is a hint, not part of the print.
      Array.prototype.forEach.call(clone.querySelectorAll('.lm-photo-sample'), function (n) { n.remove(); });
      // The IDs stay, the print's own included: the location map's CSS is
      // written against #lm-print-frame and its children (landscape sizing,
      // the Border / Fade / Circle layouts, the caption's type sizes). The
      // clone is appended after the page print and emptied on close, so
      // getElementById still finds the live one.
      // The frame's own box also depends on where it sits (Print and Canvas
      // drop the moulding through a :has() on .lm-wrapper, phones pin the
      // border width), which the clone, parked on <body>, can't match: copy
      // the computed box across.
      var fcs = getComputedStyle(srcFrame);
      ['border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width',
       'border-top-style', 'border-right-style', 'border-bottom-style', 'border-left-style',
       'border-top-color', 'border-right-color', 'border-bottom-color', 'border-left-color',
       'border-image-source', 'border-image-slice', 'border-image-width', 'border-image-outset', 'border-image-repeat',
       'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
       'background-color', 'background-image', 'background-size', 'background-position', 'border-radius', 'box-sizing'
      ].forEach(function (prop) { clone.style.setProperty(prop, fcs.getPropertyValue(prop), 'important'); });
      // The caption too: its type sizes and band position come partly from
      // rules keyed on the layout and the page around the print (e.g. the
      // Circle / Heart title size), so copy the computed values element by
      // element across the caption's subtree.
      var CAPTION_PROPS = ['font-size', 'line-height', 'letter-spacing', 'font-family', 'font-weight', 'font-style', 'text-transform', 'color',
        'margin-top', 'margin-right', 'margin-bottom', 'margin-left', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
        'top', 'right', 'bottom', 'left', 'width', 'height', 'max-width', 'text-align', 'background-color', 'background-image', 'opacity'];
      var srcCap = srcFrame.querySelector('#lm-text-overlay');
      var dstCap = clone.querySelector('#lm-text-overlay');
      if (srcCap && dstCap) {
        var srcEls = [srcCap].concat(Array.prototype.slice.call(srcCap.querySelectorAll('*')));
        var dstEls = [dstCap].concat(Array.prototype.slice.call(dstCap.querySelectorAll('*')));
        for (var ci = 0; ci < srcEls.length && ci < dstEls.length; ci++) {
          if (srcEls[ci].namespaceURI !== 'http://www.w3.org/1999/xhtml') continue;   // SVG handwriting keeps its own
          var ccs = getComputedStyle(srcEls[ci]);
          for (var pi = 0; pi < CAPTION_PROPS.length; pi++) {
            dstEls[ci].style.setProperty(CAPTION_PROPS[pi], ccs.getPropertyValue(CAPTION_PROPS[pi]), 'important');
          }
        }
      }
      clone.style.setProperty('width', fwPx + 'px', 'important');
      clone.style.setProperty('height', fhPx + 'px', 'important');
      clone.style.setProperty('left', ring + 'px', 'important');
      clone.style.setProperty('top', ring + 'px', 'important');
      // Canvases clone blank. Copy the pixels across (the moon renderer keeps
      // its drawing buffer for export, so the WebGL canvas reads back).
      var srcCanvases = srcFrame.querySelectorAll('canvas');
      var dstCanvases = clone.querySelectorAll('canvas');
      for (var i = 0; i < srcCanvases.length && i < dstCanvases.length; i++) {
        var sc = srcCanvases[i], dc = dstCanvases[i];
        if (!sc.width || !sc.height) continue;
        dc.width = sc.width; dc.height = sc.height;
        try { dc.getContext('2d').drawImage(sc, 0, 0); } catch (e) { /* noop */ }
      }
      // The map itself: a snapshot at the live canvas's full (supersampled)
      // resolution in place of the cloned Mapbox canvas, which sits in a 2-3x
      // container scaled down and draws only in part once scaled again here.
      // Sharp up to the viewer's zoom; icons and text stay cloned DOM.
      var liveMap = srcFrame.querySelector('#lm-map .mapboxgl-canvas');
      var cloneInner = clone.querySelector('#lm-map-inner');
      if (liveMap && cloneInner && liveMap.width) {
        var mapSnap = document.createElement('canvas');
        mapSnap.className = 'lm-viewer-snap';
        var liveGl = null;
        try { liveGl = liveMap.getContext('webgl2') || liveMap.getContext('webgl'); } catch (e) { /* noop */ }
        mapSnap.width = (liveGl && liveGl.drawingBufferWidth) || liveMap.width;
        mapSnap.height = (liveGl && liveGl.drawingBufferHeight) || liveMap.height;
        try { copyMapCanvas(liveMap, mapSnap.getContext('2d'), mapSnap.width, mapSnap.height); } catch (e) { /* noop */ }
        cloneInner.insertBefore(mapSnap, cloneInner.firstChild);
        var cloneMap = clone.querySelector('#lm-map .mapboxgl-canvas');
        if (cloneMap) cloneMap.style.visibility = 'hidden';
      }
      canvas.innerHTML = '';
      canvas.style.width = baseW + 'px';
      canvas.style.height = baseH + 'px';
      canvas.appendChild(clone);
      var bw = ['top', 'right', 'bottom', 'left'].map(function (s) { return parseFloat(fcs.getPropertyValue('border-' + s + '-width')) || 0; });
      var ringCanvas = paintFrameRing(fcs, fwPx, fhPx, bw, function () {
        clone.style.setProperty('transition', 'none', 'important');
        clone.style.setProperty('border-image', 'none', 'important');
        clone.style.setProperty('border-color', 'transparent', 'important');
      });
      if (ringCanvas) {
        ringCanvas.style.left = ring + 'px';
        ringCanvas.style.top = ring + 'px';
        canvas.appendChild(ringCanvas);
      }
      return true;
    }

    function measureFit() {
      var r = stage.getBoundingClientRect();
      var pad = r.width < 600 ? 24 : 64;
      fit = Math.min((r.width - pad) / baseW, (r.height - pad) / baseH);
      if (!(fit > 0)) fit = 1;
    }

    function clampPan() {
      var r = stage.getBoundingClientRect();
      var maxX = Math.max(0, (baseW * fit * zoom - r.width) / 2);
      var maxY = Math.max(0, (baseH * fit * zoom - r.height) / 2);
      tx = Math.max(-maxX, Math.min(maxX, tx));
      ty = Math.max(-maxY, Math.min(maxY, ty));
    }

    var zoomOutBtn = viewer.querySelector('[data-lm-viewer-zoom="out"]');
    var zoomInBtn = viewer.querySelector('[data-lm-viewer-zoom="in"]');
    var paintedZoom = null;
    var dismissY = 0;        // swipe-down-to-close offset (only at 100%)
    function setTransform() {
      canvas.style.transform = 'translate(-50%, -50%) translate(' + tx + 'px, ' + (ty + dismissY) + 'px) scale(' + (fit * zoom) + ')';
    }
    function apply(animate) {
      clampPan();
      viewer.classList.toggle('is-animating', !!animate);
      setTransform();
      var pct = Math.round(zoom * 100);
      if (pct !== paintedZoom) {
        paintedZoom = pct;
        viewer.classList.toggle('is-zoomed', zoom > 1.001);
        if (level) level.textContent = pct + '%';
        if (zoomOutBtn) zoomOutBtn.disabled = zoom <= MIN + 0.001;
        if (zoomInBtn) zoomInBtn.disabled = zoom >= MAX - 0.001;
      }
    }

    // Zoom to `next`, keeping the stage point (px, py), measured from the
    // stage centre, fixed under the finger/cursor.
    function zoomTo(next, px, py, animate) {
      next = Math.max(MIN, Math.min(MAX, next));
      var k = next / zoom;
      tx = px - (px - tx) * k;
      ty = py - (py - ty) * k;
      zoom = next;
      apply(animate);
    }

    function stagePoint(clientX, clientY) {
      var r = stage.getBoundingClientRect();
      return { x: clientX - r.left - r.width / 2, y: clientY - r.top - r.height / 2 };
    }

    function step(dir) {
      var next = zoom;
      if (dir > 0) {
        for (var i = 0; i < STEPS.length; i++) if (STEPS[i] > zoom + 0.01) { next = STEPS[i]; break; }
      } else {
        for (var j = STEPS.length - 1; j >= 0; j--) if (STEPS[j] < zoom - 0.01) { next = STEPS[j]; break; }
      }
      zoomTo(next, 0, 0, true);
    }

    function open(e) {
      if (!buildClone()) return;
      returnFocus = e && e.currentTarget ? e.currentTarget : document.activeElement;
      viewer.hidden = false;
      prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      zoom = 1; tx = 0; ty = 0;
      measureFit();
      apply(false);
      requestAnimationFrame(function () { viewer.classList.add('is-open'); });
      var closeBtn = viewer.querySelector('[data-lm-viewer-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      document.addEventListener('keydown', onKey);
      window.addEventListener('resize', onResize);
    }

    function close() {
      if (viewer.hidden) return;
      if (fsElement() === viewer) {
        try { (document.exitFullscreen || document.webkitExitFullscreen).call(document); } catch (e) { /* noop */ }
      }
      viewer.classList.remove('is-open', 'is-zoomed', 'is-dragging', 'is-animating', 'is-fullscreen');
      viewer.hidden = true;
      canvas.innerHTML = '';
      document.documentElement.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      pointers = {};
      if (returnFocus && returnFocus.focus && returnFocus.offsetParent !== null) {
        returnFocus.focus({ preventScroll: true });
      }
    }

    function onResize() {
      if (viewer.hidden) return;
      measureFit();
      apply(false);
    }

    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault(); step(1);
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault(); step(-1);
      } else if (e.key === '0') {
        e.preventDefault(); zoom = 1; tx = 0; ty = 0; apply(true);
      } else if (e.key === 'Tab') {
        // Keep focus inside the dialog.
        var f = viewer.querySelectorAll('button:not([disabled]):not([hidden])');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    Array.prototype.forEach.call(openers, function (btn) {
      btn.addEventListener('click', open);
    });
    viewer.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-lm-viewer-close],[data-lm-viewer-zoom]') : null;
      if (!t) return;
      if (t.hasAttribute('data-lm-viewer-close')) { close(); return; }
      var z = t.getAttribute('data-lm-viewer-zoom');
      if (z === 'in') step(1);
      else if (z === 'out') step(-1);
      else if (z === 'reset') { zoom = 1; tx = 0; ty = 0; apply(true); }
    });

    if (fsBtn && fsSupported) {
      fsBtn.addEventListener('click', function () {
        try {
          if (fsElement()) (document.exitFullscreen || document.webkitExitFullscreen).call(document);
          else (viewer.requestFullscreen || viewer.webkitRequestFullscreen).call(viewer);
        } catch (e) { /* noop */ }
      });
      var onFs = function () {
        var isFs = fsElement() === viewer;
        viewer.classList.toggle('is-fullscreen', isFs);
        fsBtn.setAttribute('aria-label', isFs ? 'Exit full screen' : 'Enter full screen');
        requestAnimationFrame(onResize);
      };
      document.addEventListener('fullscreenchange', onFs);
      document.addEventListener('webkitfullscreenchange', onFs);
    }

    // ── Gestures: drag to pan, pinch to zoom, double-tap to toggle ─────────
    var pointers = {};
    var dismissStart = null; // { x, y, t } of the finger that may be dismissing
    var pinchStart = null;   // { dist, zoom }
    var dragLast = null;     // { x, y }
    var lastTap = 0;
    var clickStart = null;   // mouse press { x, y }: a click without a drag toggles zoom

    function pointerList() {
      var out = [];
      for (var k in pointers) if (Object.prototype.hasOwnProperty.call(pointers, k)) out.push(pointers[k]);
      return out;
    }

    // The fade runs on a dedicated backdrop layer and on the bar/hint's own
    // opacity, so the drag only costs a transform and an opacity per frame.
    var backdrop = viewer.querySelector('.lm-viewer__backdrop');
    var barEl = viewer.querySelector('.lm-viewer__bar');
    var dismissRaf = 0;
    function setDim(p) {
      if (backdrop) backdrop.style.opacity = String(1 - p * 0.85);
      var o = String(Math.max(0, 1 - p * 1.4));
      if (barEl) barEl.style.opacity = o;
      if (hint) hint.style.opacity = o;
    }

    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      try { stage.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var list = pointerList();
      viewer.classList.remove('is-animating');
      if (list.length === 2) {
        if (dismissY) { dismissY = 0; setDim(0); apply(true); }
        dismissStart = null;
        pinchStart = {
          dist: Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y) || 1,
          zoom: zoom
        };
        dragLast = null;
      } else if (list.length === 1) {
        dragLast = { x: e.clientX, y: e.clientY };
        dismissStart = zoom <= 1.001 ? { x: e.clientX, y: e.clientY, t: Date.now(), active: false } : null;
        var now = Date.now();
        // A mouse click toggles zoom on release (the stage shows a zoom-in
        // cursor, and a double-click was the only thing that zoomed on
        // desktop); double-tap stays the gesture for touch.
        if (e.pointerType === 'mouse') {
          clickStart = { x: e.clientX, y: e.clientY };
        } else if (now - lastTap < 300) {
          var p = stagePoint(e.clientX, e.clientY);
          if (zoom > 1.001) { zoom = 1; tx = 0; ty = 0; apply(true); }
          else zoomTo(2.5, p.x, p.y, true);
          lastTap = 0;
          dragLast = null;
        } else {
          lastTap = now;
        }
      }
    });

    stage.addEventListener('pointermove', function (e) {
      if (!pointers[e.pointerId]) return;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var list = pointerList();
      if (list.length >= 2 && pinchStart) {
        var d = Math.hypot(list[0].x - list[1].x, list[0].y - list[1].y) || 1;
        var mid = stagePoint((list[0].x + list[1].x) / 2, (list[0].y + list[1].y) / 2);
        zoomTo(pinchStart.zoom * d / pinchStart.dist, mid.x, mid.y, false);
      } else if (list.length === 1 && dragLast) {
        var dx = e.clientX - dragLast.x, dy = e.clientY - dragLast.y;
        if (Math.abs(dx) + Math.abs(dy) > 2) lastTap = 0;
        dragLast = { x: e.clientX, y: e.clientY };
        if (zoom > 1.001) {
          viewer.classList.add('is-dragging');
          tx += dx; ty += dy;
          apply(false);
        } else if (dismissStart) {
          // At 100% a mostly-downward drag pulls the print away: it follows
          // the finger and the backdrop fades, like Photos on iPhone.
          var ddx = e.clientX - dismissStart.x, ddy = e.clientY - dismissStart.y;
          if (!dismissStart.active && ddy > 8 && ddy > Math.abs(ddx) * 1.2) dismissStart.active = true;
          if (dismissStart.active) {
            dismissY = Math.max(0, ddy);
            if (!dismissRaf) {
              canvas.style.willChange = 'transform';
              viewer.classList.remove('is-animating');
              dismissRaf = requestAnimationFrame(function () {
                dismissRaf = 0;
                setTransform();
                setDim(Math.min(1, dismissY / 300));
              });
            }
          }
        }
      }
    });

    function endPointer(e) {
      if (!pointers[e.pointerId]) return;
      if (clickStart && e.type === 'pointerup' && !(dismissStart && dismissStart.active) &&
          Math.abs(e.clientX - clickStart.x) + Math.abs(e.clientY - clickStart.y) < 5) {
        var cp = stagePoint(e.clientX, e.clientY);
        if (zoom > 1.001) { zoom = 1; tx = 0; ty = 0; apply(true); }
        else zoomTo(2.5, cp.x, cp.y, true);
      }
      clickStart = null;
      if (dismissStart && dismissStart.active) {
        if (dismissRaf) { cancelAnimationFrame(dismissRaf); dismissRaf = 0; }
        setTimeout(function () { canvas.style.willChange = ''; }, 260);
        var dt = Math.max(1, Date.now() - dismissStart.t);
        var velocity = dismissY / dt; // px per ms
        dismissStart = null;
        if (dismissY > 110 || (dismissY > 40 && velocity > 0.6)) {
          // Carry on off the bottom, then close.
          dismissY = window.innerHeight;
          setDim(1);
          apply(true);
          setTimeout(function () { close(); dismissY = 0; setDim(0); }, 200);
        } else {
          dismissY = 0;
          setDim(0);
          apply(true);
        }
      }
      delete pointers[e.pointerId];
      var list = pointerList();
      if (list.length < 2) pinchStart = null;
      // Lifting one finger of a pinch hands the pan to the one that stays.
      dragLast = list.length === 1 ? { x: list[0].x, y: list[0].y } : null;
      if (!list.length) viewer.classList.remove('is-dragging');
    }
    stage.addEventListener('pointerup', endPointer);
    stage.addEventListener('pointercancel', endPointer);

    stage.addEventListener('wheel', function (e) {
      e.preventDefault();
      var p = stagePoint(e.clientX, e.clientY);
      // Trackpad pinch arrives as ctrl+wheel with small deltas; a mouse wheel
      // notch is ~100. Both map to a smooth exponential zoom.
      var factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0012));
      zoomTo(zoom * factor, p.x, p.y, false);
    }, { passive: false });

    // Safari's proprietary pinch events would otherwise zoom the whole page.
    ['gesturestart', 'gesturechange'].forEach(function (t) {
      viewer.addEventListener(t, function (e) { e.preventDefault(); });
    });
  }

  // ── Phone chrome on the preview: menu + cart ────────────────────────────
  // ≤991px the header bar is collapsed (sections/moon-skylit.liquid). The
  // menu button opens the theme's own drawer; the cart button clicks the
  // hidden header icon (so the drawer behaves as ever) and mirrors the
  // header's count bubble, which the theme keeps current.
  function bindMobileChrome() {
    var btn = document.getElementById('lm-cart-btn');
    var badge = document.getElementById('lm-cart-btn-count');
    var icon = document.getElementById('cart-icon-bubble');
    var menuBtn = document.getElementById('lm-home-btn');
    var drawerToggle = document.querySelector('header-drawer summary');
    if (menuBtn && drawerToggle) {
      menuBtn.addEventListener('click', function (e) {
        e.preventDefault();
        drawerToggle.click();
      });
    }

    if (!btn || !icon) return;
    btn.addEventListener('click', function () { icon.click(); });

    function syncCount() {
      if (!badge) return;
      var bubble = icon.querySelector('.cart-count-bubble');
      // The bubble holds the count twice: the visible digit (span[aria-hidden])
      // and screen-reader text ("5 items"). Take the visible number.
      var n = 0;
      if (bubble) {
        var vis = bubble.querySelector('span[aria-hidden="true"]');
        var m = ((vis ? vis.textContent : bubble.textContent) || '').match(/\d+/);
        n = m ? parseInt(m[0], 10) : 0;
      }
      badge.hidden = n === 0;
      badge.textContent = n > 99 ? '99+' : String(n);
      btn.setAttribute('aria-label', n ? 'Open cart, ' + n + (n === 1 ? ' item' : ' items') : 'Open cart');
    }
    syncCount();
    if (window.MutationObserver) {
      new MutationObserver(syncCount).observe(icon, { childList: true, subtree: true, characterData: true });
    }
  }

  // The toolbar uses the same preference and controller as the storefront.
  function bindThemeToggle() {
    var btn = document.getElementById('lm-toolbar-theme');
    if (btn && window.SkylitTheme) window.SkylitTheme.bind(btn);
  }

  // ── Add-to-cart star trail ──────────────────────────────────────────────
  // A comet flies from the button into the basket icon while the cart
  // requests run (assets/skylit-star-trail.js); moon-myjs.js's submit_form
  // calls these. The redirect waits for it to land, a failed add cancels it.
  // Edit mode replaces the design's existing cart line rather than adding
  // one, so the comet still flies but the count stays put.
  var cartFlight = null;
  function launchCartFlight(fromEl, editMode) {
    cartFlight = null;
    if (!window.SkylitStarTrail || !fromEl) return;
    cartFlight = window.SkylitStarTrail.launch(fromEl, [
      document.getElementById('lm-cart-btn'),       // phones: bag on the preview
      document.getElementById('cart-icon-bubble')     // desktop: header bag
    ], {
      onLand: function () {
        if (editMode) return;
        var badge = document.getElementById('lm-cart-btn-count');
        if (!badge) return;
        var n = (parseInt(badge.textContent, 10) || 0) + 1;
        badge.textContent = n > 99 ? '99+' : String(n);
        badge.hidden = false;
      }
    });
  }
  function landCartFlight() {
    var f = cartFlight;
    cartFlight = null;
    return f ? f.land().catch(function () {}) : Promise.resolve();
  }
  function cancelCartFlight() {
    if (cartFlight) cartFlight.cancel();
    cartFlight = null;
  }

  window.LMStage = {
    launchCartFlight: launchCartFlight,
    landCartFlight: landCartFlight,
    cancelCartFlight: cancelCartFlight
  };

  function init() {
    var main = document.querySelector('.lm-main.lm-stage');
    if (!main) return;
    bindMobilePreview(main);
    bindToolsExtras(main);
    bindPrintViewer();
    bindRoomView(main);
    bindMobileChrome();
    bindThemeToggle();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
